import React, { FC, useState, useRef, useEffect } from 'react';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import { useTranslation } from 'react-i18next';
import { TDealerContactsProps } from '../../types/dealerLocatorDetail.types';
import DealerLocatorAttribute from './dealerContactAttribute';
import Icon from '../../../@shared/icon';
import { iconSize } from '../../../../constants/icon.constants';
import { googleStaticMapUrl, gooleMapUrl, maxZoomSize, staticMapInitialZoom } from '../../../../constants/map.constatnts';
import RoutableNotEditableLink from '../../../@shared/routableNotEditableLink';
import { BreakpointsInPx } from '../../../../constants/breakpoints.constants';
import dealerPinSvg from '../../../../assets/pin-dealer-detail.svg';
import useMediaQuery from '../../../../lib/custom-hooks/useMediaQuery';
import { windowService } from '../../../../utils/window.utils';

const DealerLocatorContacts: FC<TDealerContactsProps> = ({ addressDetails, apiKey, latitude, longitude }) => {
  const { t } = useTranslation();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(staticMapInitialZoom);
  const isDesktop = useMediaQuery(`(min-width: ${BreakpointsInPx['md']})`);
  const [mapWidth, setMapWidth] = useState<number>(392);
  const [mapVisisble, setMapVisibility] = useState<boolean>(false);
  const mapHeight = isDesktop ? 392 : 343;

  const updateMapWidth = () => {
    if (!isDesktop && mapContainerRef?.current) {
      setMapWidth(mapContainerRef?.current?.clientWidth);
    }
  };

  useEffect(() => {
    updateMapWidth();
    if (!mapVisisble) {
      setMapVisibility(true);
    }
  }, [mapContainerRef]);

  useEffect(() => {
    windowService()?.addEventListener('resize', updateMapWidth);
    return () => {
      windowService()?.removeEventListener('resize', updateMapWidth);
    };
  }, []);

  return (
    <section className="dealer-locator-detail__contacts">
      <div className="dealer-locator-detail__contacts--wrapper">
        <div className="dealer-locator-detail__contacts__details">
          <Text
            field={{ value: t('cnhi-dealerLocatorDealerContacts') }}
            tag="div"
            editable={false}
            className={'dealer-locator-detail__contacts__title'}
          />
          {addressDetails?.address && (
            <Text
              field={{ value: addressDetails?.address }}
              tag="div"
              editable={false}
              className={'dealer-locator-detail__contacts__address'}
            />
          )}
          <RoutableNotEditableLink
            target={'_blank'}
            href={`${gooleMapUrl}${latitude},${longitude}`}
            className={'dealer-locator-detail__contacts__directions'}
          >
            <Text field={{ value: t('cnhi-dealerLocatorGetDirections') }} tag="div" editable={false} />
            <Icon iconName="geolocation-distance-full" color={'primary'} size={iconSize['mbl']} />
          </RoutableNotEditableLink>
          <section className={'dealer-locator-detail__contacts__attributes--wrapper'}>
            {addressDetails?.fax && (
              <DealerLocatorAttribute
                hasUnderline={false}
                href={`fax:${addressDetails?.fax}`}
                iconName={'file-print'}
                attribute={addressDetails?.fax}
              />
            )}
            {addressDetails?.phone && (
              <DealerLocatorAttribute
                hasUnderline={false}
                href={`tel:${addressDetails?.phone}`}
                iconName={'phone-messages-phone'}
                attribute={addressDetails?.phone}
              />
            )}
            {addressDetails?.email && (
              <DealerLocatorAttribute
                hasUnderline={true}
                href={`mailto:${addressDetails?.email}`}
                iconName={'social-sharing-email'}
                attribute={addressDetails?.email}
              />
            )}
            {addressDetails?.website && (
              <DealerLocatorAttribute
                hasUnderline={true}
                href={addressDetails?.website}
                iconName={'social-sharing-globe'}
                attribute={addressDetails?.website}
                openInNewTab={true}
              />
            )}
          </section>
        </div>
        <div className="dealer-locator-detail__contacts__map" ref={mapContainerRef}>
          <img
            className={`dealer-locator-detail__contacts__map__image ${
              mapVisisble ? 'dealer-locator-detail__contacts__map__image--visible' : ''
            }`}
            src={`${googleStaticMapUrl}?center=${latitude},${longitude}&zoom=${zoom}&size=${mapWidth}x${mapHeight}&key=${apiKey}`}
            alt=""
          />
          <RoutableNotEditableLink
            target={'_blank'}
            href={`${gooleMapUrl}${latitude},${longitude}`}
            className="dealer-locator-detail__map-icon dealer-locator-detail__map-icon--get-location"
          >
            <img src={dealerPinSvg} alt="" />
          </RoutableNotEditableLink>
          <div
            className="dealer-locator-detail__map-icon dealer-locator-detail__map-icon--zoom-in"
            onClick={() => zoom < maxZoomSize && setZoom(zoom + 1)}
          >
            <Icon color={'light-black'} iconName="action-plus" size={iconSize['lg']} />
          </div>
          <div
            className="dealer-locator-detail__map-icon dealer-locator-detail__map-icon--zoom-out"
            onClick={() => zoom > 1 && setZoom(zoom - 1)}
          >
            <Icon color="light-black" iconName="action-minus" size={iconSize['lg']} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default DealerLocatorContacts;
